import * as React from "react";
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { IconButton, InputAdornment } from "@material-ui/core";
import ReplayIcon from '@material-ui/icons/Replay';
import { defaultInputProps, useRootStyles } from "./globalprops"
import { Loading } from "./Loading";
import { ICommonSelectControlsWithLabel } from "./ICommonInputsControls";
import { useTranslation } from 'react-i18next';



export interface IOption {
    value: string;
    label?: string;
    enabled?: boolean;
}

interface IExtendedOption<T extends IOption> {
    isFromSelectedList: boolean;
    value: T;
}

interface IProps {
    freeSolo?: boolean;
}

export type AllProps<T extends IOption> = IProps & ICommonSelectControlsWithLabel<T>;

export const Select = <T extends IOption>(props: AllProps<T>) => {
    
    const classes = useRootStyles(useTheme());
    const { t } = useTranslation('common');
    

    const [selectedItem, setSelectedItem] = React.useState<T | null>(props.options.find(value => value.value === props.value?.value) || props.value || null);
    const [valueList, setValueList] = React.useState<IExtendedOption<T>[]>(props.options.map(value => ({value: value, isFromSelectedList: false})));
    const [inputValue, setInputValue] = React.useState<string>(props.value?.label || props.value?.value || '');

    // Add selected values that are not in the value list
    if (selectedItem != null && !valueList.some(value => value.value.value == selectedItem.value)) {
        setValueList([...valueList, {value: {...selectedItem, label: selectedItem.label ?? selectedItem.value, enabled: props.isUnknownValuesEnabled ?? false}, isFromSelectedList: true}]);
    }

    const valueT:T[] = valueList.map(value => value.value);

    const validateValue = (newValue: T | null) => props.onValidate ? props.onValidate(newValue) : [false, ""];
    const [error, setError] = React.useState(props.error || [false, ""]);


    const onUpdate = (newValue: T | null) => {
        setSelectedItem(newValue);
        const theError = validateValue(newValue);
        setError(theError);
        props.onChange && props.onChange(theError[0] ? null : newValue, theError[0] === false);
    }

    // Handle blur event for freeSolo to commit typed value
    const handleBlur = () => {
        if (props.freeSolo && inputValue && inputValue.trim()) {
            // Check if input matches current selected item
            const currentLabel = selectedItem?.label || selectedItem?.value || '';
            if (inputValue !== currentLabel) {
                // User typed something different, commit it
                const customOption = { value: inputValue.trim(), label: inputValue.trim() } as T;
                onUpdate(customOption);
            }
        }
    };

    React.useEffect(() => {
        props.prevalidate && onUpdate(selectedItem);
    }, []);

    // Update selected item when props.value or props.options change
    React.useEffect(() => {
        const newSelectedItem = props.options.find(value => value.value === props.value?.value) || props.value || null;
        setSelectedItem(newSelectedItem);
        setInputValue(newSelectedItem?.label || newSelectedItem?.value || '');
    }, [props.value, props.options]);

    // Update value list when options change
    React.useEffect(() => {
        setValueList(props.options.map(value => ({value: value, isFromSelectedList: false})));
    }, [props.options]);

    const errorStuff = error && error[0] ? ({
        error: true,
        helperText: error[1]
    }) : undefined;


    return props.loading? <Loading label={props.label ?? ""} /> : <Autocomplete
        className={classes.root}
        options={valueT}
        freeSolo={props.freeSolo}
        disabled={props.disabled}
        getOptionLabel={(option) => {
            // When freeSolo is enabled, option can be a string
            if (typeof option === 'string') return option;
            return option.label || option.value;
        }}
        getOptionDisabled={(option) => {
            // Strings don't have enabled property
            if (typeof option === 'string') return false;
            return option.enabled === false;
        }}
        value={selectedItem}
        inputValue={inputValue}
        onChange={(event, newValue) => {
            // When freeSolo is enabled, newValue can be a string
            if (props.freeSolo && typeof newValue === 'string') {
                // Convert string to IOption object
                const customOption = { value: newValue, label: newValue } as T;
                onUpdate(customOption);
                setInputValue(newValue);
            } else if (typeof newValue === 'string') {
                // freeSolo not enabled but got string (shouldn't happen)
                setInputValue(newValue);
            } else {
                // It's an IOption or null
                onUpdate(newValue as T | null);
                if (newValue) {
                    setInputValue(newValue.label || newValue.value);
                } else {
                    setInputValue('');
                }
            }
        }}
        onInputChange={(event, newInputValue, reason) => {
            // Update input value as user types
            if (reason === 'input') {
                setInputValue(newInputValue);
            } else if (reason === 'reset') {
                // When option is selected, update input value
                setInputValue(newInputValue);
            } else if (reason === 'clear') {
                setInputValue('');
                onUpdate(null);
            }
        }}
        onBlur={handleBlur}
        renderInput={(params) => 
            
            error[0]

            ? <TextField
            {...params }
            {...defaultInputProps}
            {...errorStuff}
            required={props.required}
            label={props.label}
            placeholder={"Probeer opnieuw"}
           
          /> 

          : <TextField
            {...params }
            {...defaultInputProps}
            required={props.required}
            label={props.label}
            placeholder={props.placeholder || (props.freeSolo ? `${t('select')} of typ...` : `${t('select')}...`)}
          />
            }
    />
}

export default Select;